/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Answer } from './api';
import {
  FeedbackSectionId,
  parseString,
  renderAnsweredQuestions,
} from './utils';
import { FormatUTC } from 'shared/utils/formatUTC';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { camelCaseToSpacedCase } from 'shared/utils/stringUtils';
import { INDICATOR_SUMMARY_QUESTION_ID } from 'npo/pages/NPOFeedback/utils';
import {
  DIRECT_COST_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
} from 'npo/pages/ReportBuilder/Components/Budget/utils';
import RenderBudgetAnswer from './RenderBudgetAnswer';
import TrueImpactJSON from 'shared/design-system/components/TrueImpactJSON';
import RenderLocationAnswer from './RenderLocationAnswer';
import RenderModel from './RenderModel';

const expended = () => true;

interface SingleAnswerProps {
  id: string;
  value: string | number;
}

const style = { marginBottom: 4 };
const isLink = (val: number | string) =>
  typeof val === 'string' && val.startsWith('https://');
const SingleAnswer: React.FC<SingleAnswerProps> = ({ id, value }) => {
  return (
    <TrueImpactText variant="body-small" style={style}>
      <strong>{camelCaseToSpacedCase(id)}:</strong>{' '}
      {id === 'currency' ? '$' : ''}
      {isLink(value) ? (
        <a href={value.toString()} target="_blank" rel="noreferrer">
          {value}
        </a>
      ) : (
        value
      )}
    </TrueImpactText>
  );
};

interface RenderAnswersProps {
  answer:
    | Answer
    | {
        text?: string;
        date1?: string;
        date2?: string;
        question: { dbId: number };
      };
  reportId: number;
  questionId: number;
  expandedByDefault?: boolean;
  sectionId: FeedbackSectionId;
}

const StyledDiv = styled.div`
  margin-top: ${SPACERS.tiny};
  .date {
    display: flex;
    align-items: center;
    .date1 {
      margin-right: ${SPACERS.small};
    }
  }
`;

const budgetQuestionIds = [
  DIRECT_COST_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
];

const RenderAnswers: React.FC<RenderAnswersProps> = ({
  answer,
  reportId,
  sectionId,
  questionId,
  expandedByDefault,
}) => {
  if (answer.date1 || answer.date2) {
    return (
      <StyledDiv>
        <div className="date">
          <TrueImpactText variant="body-small" className="date1">
            <strong className="answer-label">
              {answer.date1 && answer.date2
                ? 'Start Date'
                : 'Planning Start Date'}
              :
            </strong>{' '}
            {answer.date1
              ? new FormatUTC(answer.date1).formatDate()
              : 'Not selected'}
          </TrueImpactText>
          {answer.date2 && (
            <TrueImpactText variant="body-small" className="date2">
              <strong className="answer-label">End Date:</strong>{' '}
              {new FormatUTC(answer.date2).formatDate()}
            </TrueImpactText>
          )}
        </div>
      </StyledDiv>
    );
  }

  if (!answer.text) return null;

  const parseObject = parseString(answer.text);
  if (parseObject && typeof parseObject === 'object') {
    const shouldExpend =
      expandedByDefault === true
        ? expended
        : expandedByDefault === false
        ? undefined
        : answer.question.dbId === INDICATOR_SUMMARY_QUESTION_ID
        ? undefined
        : expended;
    if (
      budgetQuestionIds.includes(answer.question.dbId) &&
      !(parseObject as any).currency
    ) {
      return (
        <RenderBudgetAnswer
          answer={answer}
          reportId={reportId}
          parseObject={parseObject}
        />
      );
    }

    if (sectionId === 'location') {
      return <RenderLocationAnswer parseObject={parseObject as any} />;
    }

    if (questionId === INDICATOR_SUMMARY_QUESTION_ID) {
      return (
        <RenderModel
          questionId={questionId}
          reportId={reportId}
          sectionId={sectionId}
          indicatorDetails={parseObject as any}
        />
      );
    }

    if ('catalyzed' in parseObject) {
      delete parseObject.catalyzed;
    }
    return (
      <TrueImpactJSON shouldExpend={shouldExpend} parseObject={parseObject} />
    );
  }

  const text = renderAnsweredQuestions(answer.text);

  if (typeof text === 'string' || typeof text === 'number') {
    return (
      <StyledDiv>
        <TrueImpactText variant="body-small">
          <strong className="answer-label">Answer:</strong> {text}
        </TrueImpactText>
      </StyledDiv>
    );
  }

  return (
    <StyledDiv>
      {text.map(t => (
        <SingleAnswer key={t.id} id={t.id} value={t.value} />
      ))}
    </StyledDiv>
  );
};

export default RenderAnswers;
